import { createContext, useContext, type ReactNode } from 'react';

// ════════════════════════════════════════════════════════════
// RESINCRONIZACIÓN DEL DOCUMENTO — salida pública para el host
// ════════════════════════════════════════════════════════════
//
// El `documentState` (headerValues, `_lines`, contador de líneas, totales) vive
// en el reducer de `SmartMasterDocument`, NO en `MasterDocumentProvider`: ese
// provider solo publica adapter, template, iconos, widgets y permisos. Por eso
// el kit mergea una línea nueva únicamente cuando la guarda ÉL —desde su propio
// modal de línea, vía `adapter.saveLine`— y un componente del host que persista
// por su cuenta (un widget del FactBox que cruza una OC, una pestaña de detalle
// que anula una línea contra su propio endpoint) dejaba la pantalla mostrando
// los valores previos hasta que el usuario recargaba con F5.
//
// El kit ya sabía resincronizarse: `refetchDocument` (LOAD_START →
// `adapter.getDocument` → LOAD_SUCCESS → `refreshToken++`) es lo que corre tras
// un conflicto 409, tras una acción de línea genérica y tras anexar líneas desde
// un origen. Lo único que faltaba era publicarlo. Esto lo publica, sin abrir el
// `dispatch` del reducer: el host puede pedir "vuelve a leer el documento", no
// escribir estado a mano.

/**
 * Resincronización del documento en pantalla, disponible para cualquier
 * componente montado dentro de `SmartMasterDocument`.
 *
 * @see useMasterDocumentReload - El caso de uso normal (solo `reload`).
 * @see FactBoxWidgetRenderContext - Los widgets custom lo reciben ya en su contexto.
 */
export interface MasterDocumentRefreshApi {
  /**
   * Vuelve a leer el documento del servidor y reemplaza el estado del kit con la
   * respuesta: encabezado, líneas, totales, `docState` y `row_version`. Es la
   * MISMA operación que dispara "Recargar documento" del modal de conflicto.
   *
   * Descarta la edición en vuelo del encabezado (lo escrito y no guardado), así
   * que se invoca DESPUÉS de persistir, no antes. Resuelve cuando el estado ya
   * se aplicó; si la lectura falla, el error se reporta por el mismo canal que
   * la carga inicial (toast + log) y la promesa igual resuelve.
   */
  reload: () => Promise<void>;
  /**
   * Contador que sube tras cada mutación del kit (guardar, borrar o duplicar una
   * línea, acción de fila, `reload`). Úsalo como dependencia del fetch propio del
   * host —una React Query key, un `useEffect`— para refrescar SU listado cuando
   * el documento cambió por fuera de él. Es el mismo valor que reciben las
   * pestañas de detalle en `LineTabRenderContext.refreshToken`.
   */
  refreshToken: number;
}

const RefreshContext = createContext<MasterDocumentRefreshApi | null>(null);

/**
 * `reload` inerte para los dos casos en que no hay documento montado: el hook
 * llamado fuera de `SmartMasterDocument` y el valor por defecto del builder del
 * FactBox. Avisa por consola en vez de lanzar —un widget del host no debe tumbar
 * la pantalla por estar mal cableado— y es una constante de módulo para que su
 * identidad sirva como dependencia estable.
 */
export const INERT_REFRESH: MasterDocumentRefreshApi = {
  reload: async () => {
    console.warn(
      '[MasterDocument] reload() fuera de <SmartMasterDocument>: no hay documento que recargar.',
    );
  },
  refreshToken: 0,
};

/**
 * API completa de resincronización, o `null` fuera de un `SmartMasterDocument`
 * (mismo contrato nullable que `useMasterDocumentPrint`/`useMasterDocumentAudit`:
 * subsistema ausente ⇒ contexto en null, nunca excepción).
 *
 * Úsalo cuando necesites `refreshToken` además de `reload`; si solo vas a
 * recargar, `useMasterDocumentReload()` evita el chequeo de null.
 */
export function useMasterDocumentRefresh(): MasterDocumentRefreshApi | null {
  return useContext(RefreshContext);
}

/**
 * Recarga el documento del `SmartMasterDocument` que envuelve al componente.
 *
 * Pensado para el host que persiste por su cuenta y necesita que la pantalla se
 * entere: widgets custom del FactBox, renderers de pestañas de detalle, paneles
 * propios montados en cualquier slot del kit.
 *
 * Siempre devuelve una función invocable: fuera del árbol de un documento es un
 * no-op que avisa por consola, para que el consumidor no tenga que ramificar.
 *
 * @example
 * ```tsx
 * function CrossingsWidget() {
 *   const reload = useMasterDocumentReload();
 *   const onAccept = async () => {
 *     await api.crossLines(...);   // persistencia propia del host
 *     await reload();              // el kit vuelve a leer líneas y totales
 *   };
 *   // ...
 * }
 * ```
 */
export function useMasterDocumentReload(): () => Promise<void> {
  const api = useContext(RefreshContext);
  return api?.reload ?? INERT_REFRESH.reload;
}

interface MasterDocumentRefreshProviderProps {
  value: MasterDocumentRefreshApi;
  children: ReactNode;
}

/**
 * Publica la resincronización a todo el subárbol del documento. Lo monta
 * `SmartMasterDocument` —el único que tiene el reducer y por tanto lo único que
 * puede recargar de verdad— y por eso NO se exporta al consumidor: montarlo a
 * mano obligaría al host a fabricar un `reload`, que es justo lo que no puede.
 */
export function MasterDocumentRefreshProvider({
  value,
  children,
}: MasterDocumentRefreshProviderProps) {
  return <RefreshContext.Provider value={value}>{children}</RefreshContext.Provider>;
}
